import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, DataTable, EmptyState, Link, Panel, t, Text } from '@kanbios/ui';
import { DeploymentStatus } from './DeploymentStatus';
import type { maintenance } from '@kanbios/api-client';
import { RefusalPage } from './Refusal';
import { api, unwrap } from '../lib/api';
import { shortVersion } from '../lib/format';
import { useEnvironmentName } from '../lib/environments';
import { useSession } from '../lib/session';

type Item = maintenance.operations['listEnvironments']['responses']['200']['content']['application/json']['items'][number];

/**
 * SPEC-034 § 12.10, D94: the environments the platform installed for a project, in the project's « Environnements »
 * tab (the way in of a project team's member is « Projets »), and the person's role on each one.
 */
export function ProjectEnvironments({ project }: { project: string }) {
  const navigate = useNavigate();
  const envName = useEnvironmentName();
  const { enums, can } = useSession();
  const list = useQuery({
    queryKey: ['environments', project],
    queryFn: async () => unwrap(await api.maintenance.GET('/environments', { params: { query: { project } } })),
    refetchInterval: 30_000,
  });
  if (list.error) return <RefusalPage error={list.error} />;
  // Same frame as the other project tabs (Serveurs): Panel title + subtitle, flush around its table.
  return (
    <Panel title={t('environments.title')} subtitle={t('environments.subtitle')} flush>
      <DataTable
        bordered={false}
        label={t('environments.title')}
        rows={list.data?.items ?? []}
        rowKey="deployment"
        loading={list.isPending}
        toolbar={false}
        onRowClick={(r: Item) => navigate(`/environments/${encodeURIComponent(r.deployment)}`)}
        empty={(
          <EmptyState framed={false} icon="deployed_code" title={t('environments.empty')}
            action={can('project.install') ? <Button variant="secondary" size="sm" icon="dns" onClick={() => navigate('/servers')}>{t('environments.emptyAction')}</Button> : undefined}>
            {t('environments.emptyBody')}
          </EmptyState>
        )}
        columns={[
          { key: 'deployment', label: t('environments.deployment'), mono: true, grow: true },
          { key: 'environment', label: t('environments.environment'), render: (r: Item) => <Text variant="body-sm">{envName(r.environment)}</Text> },
          { key: 'environmentType', label: t('environments.type'), render: (r: Item) => <Text variant="body-sm">{enums.environmentTypes.find(e => e.value === r.environmentType)?.label ?? r.environmentType}</Text> },
          { key: 'server', label: t('environments.server'), mono: true },
          { key: 'domain', label: t('environments.address'), render: (r: Item) => <Link href={`https://${r.domain}`} external>{r.domain}</Link> },
          { key: 'release', label: t('environments.version'), render: (r: Item) => <Text variant="code">{r.release ? shortVersion(r.release) : '—'}</Text> },
          { key: 'state', label: t('environments.state'), render: (r: Item) => <DeploymentStatus state={r.state} observed={r.observed} size="sm" /> },
          { key: 'role', label: t('environments.role'), render: (r: Item) => (r.role ? <Text variant="body-sm">{t(`environments.roles.${r.role}`)}</Text> : <Text variant="body-sm" tone="mute">{t('environments.roles.reader')}</Text>) },
        ]}
      />
    </Panel>
  );
}
